import { useMemo } from 'react'
import { Table, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { DeviceActions, NODE_ICONS } from './DeviceActions'
import {
  buildInfraRow,
  isInfraProblematic,
  type InfraNode,
  type InfraTableRow,
} from './utils'
import styles from '@/pages/HomePage.module.css'

function copyIp(ip?: string) {
  if (!ip) return
  void navigator.clipboard?.writeText(ip)
}

function StatusCell({ row }: { row: InfraTableRow }) {
  const online = row.status === 'online'
  const offline = row.status === 'offline'
  const text = offline ? 'Оффлайн' : online ? 'Онлайн' : 'Неизвестно'
  return (
    <span
      className={[
        styles.statusCell,
        online ? styles.statusOn : '',
        offline ? styles.statusOff : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <i className={styles.statusDot} aria-hidden />
      <span>
        {text}
        {row.ping ? <span className={styles.statusPing}> · {row.ping}</span> : null}
      </span>
    </span>
  )
}

function buildSummaryMeta(
  row: InfraTableRow,
  utm?: Record<string, unknown>,
  chz?: Record<string, unknown>,
) {
  if (row.nodeType === 'chz' && chz && !chz.error) {
    return `v${String(chz.version ?? '—')}`
  }
  if (row.nodeType === 'egais_utm' && utm && !utm.error) {
    return `v${String(utm.version ?? '—')}${utm.contour != null ? ` · ${String(utm.contour)}` : ''}`
  }
  if ((utm?.error || chz?.error) && (row.nodeType === 'egais_utm' || row.nodeType === 'chz')) {
    return String(utm?.error || chz?.error)
  }
  return null
}

interface Props {
  nodes: InfraNode[]
  storeId: string
  utmByNode: Record<string, Record<string, unknown>>
  chzByNode: Record<string, Record<string, unknown>>
  problemsOnly: boolean
  onReload: () => void
  onSelectRow: (row: InfraTableRow) => void
}

export function InfraTable({
  nodes,
  storeId,
  utmByNode,
  chzByNode,
  problemsOnly,
  onReload,
  onSelectRow,
}: Props) {
  const rows = useMemo(() => {
    const all = nodes.map((node, idx) => buildInfraRow(node, idx))
    const filtered = problemsOnly
      ? all.filter((row) => isInfraProblematic(row, utmByNode[row.nodeId], chzByNode[row.nodeId]))
      : all
    return filtered.sort((a, b) => {
      const aOff = a.status === 'offline' ? 0 : 1
      const bOff = b.status === 'offline' ? 0 : 1
      return aOff - bOff
    })
  }, [nodes, problemsOnly, utmByNode, chzByNode])

  const columns: ColumnsType<InfraTableRow> = [
    {
      title: 'Статус',
      width: 130,
      render: (_, row) => <StatusCell row={row} />,
    },
    {
      title: 'Устройство',
      ellipsis: true,
      render: (_, row) => (
        <div className={styles.deviceCell}>
          <span className={styles.deviceCellIcon}>{NODE_ICONS[row.nodeType]}</span>
          <div className={styles.deviceCellText}>
            <div className={styles.deviceName}>{row.title}</div>
            {row.title !== row.typeHint ? (
              <div className={styles.deviceType}>{row.typeHint}</div>
            ) : null}
          </div>
        </div>
      ),
    },
    {
      title: 'IP',
      width: 140,
      render: (_, row) => (
        <Tooltip title="Скопировать">
          <button
            type="button"
            className={styles.ipBtn}
            onClick={(e) => {
              e.stopPropagation()
              copyIp(row.ip)
            }}
          >
            {row.ip || '—'}
          </button>
        </Tooltip>
      ),
    },
    {
      title: 'Инфо',
      width: 180,
      ellipsis: true,
      render: (_, row) => {
        const meta = buildSummaryMeta(row, utmByNode[row.nodeId], chzByNode[row.nodeId])
        const hasError = Boolean(utmByNode[row.nodeId]?.error || chzByNode[row.nodeId]?.error)
        if (!meta) return <span className={styles.metaMuted}>—</span>
        return (
          <span className={`${styles.metaChip} ${hasError ? styles.metaErr : ''}`} title={meta}>
            {meta}
          </span>
        )
      },
    },
    {
      title: 'Действия',
      width: 160,
      render: (_, row) => {
        const hasDetails = row.nodeType === 'chz' || row.nodeType === 'egais_utm'
        return (
          <div onClick={(e) => e.stopPropagation()} role="presentation">
            <DeviceActions
              node={row}
              id={row.nodeId}
              storeId={storeId}
              chz={chzByNode[row.nodeId]}
              onReload={onReload}
              onShowDetails={hasDetails ? () => onSelectRow(row) : undefined}
            />
          </div>
        )
      },
    },
  ]

  return (
    <Table<InfraTableRow>
      className={styles.dataTable}
      columns={columns}
      dataSource={rows}
      pagination={false}
      tableLayout="fixed"
      size="small"
      rowClassName={(row) => (row.status === 'offline' ? styles.rowOffline : '')}
      onRow={(row) => {
        const hasDetails = row.nodeType === 'chz' || row.nodeType === 'egais_utm'
        return {
          onClick: hasDetails ? undefined : () => onSelectRow(row),
          style: { cursor: hasDetails ? 'default' : 'pointer' },
        }
      }}
    />
  )
}

export type { InfraTableRow }
